import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const fmt = (v) => {
  if (v == null) return '--';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (str) => {
  if (!str) return '--';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CATEGORY_COLORS = {
  'Meat': 'bg-red-50 text-red-700',
  'Poultry': 'bg-orange-50 text-orange-700',
  'Produce': 'bg-emerald-50 text-emerald-700',
  'Dairy & Eggs': 'bg-yellow-50 text-yellow-700',
  'Dairy': 'bg-yellow-50 text-yellow-700',
  'Bakery': 'bg-amber-50 text-amber-700',
  'Beverage': 'bg-sky-50 text-sky-700',
  'Condiments': 'bg-violet-50 text-violet-700',
  'Cleaning Supplies': 'bg-cyan-50 text-cyan-700',
  'Operational Supplies': 'bg-zinc-100 text-zinc-600',
  'Pantry and Ingredients': 'bg-lime-50 text-lime-700',
  'Prepared Foods': 'bg-rose-50 text-rose-700',
  'Proteins': 'bg-red-50 text-red-700',
  'Seafood': 'bg-blue-50 text-blue-700',
  'Gas': 'bg-stone-100 text-stone-700',
};

const getCatColor = (cat) => CATEGORY_COLORS[cat] || 'bg-zinc-100 text-zinc-600';

const VendorCatalogView = ({ loaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/v3/purchases')
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.error || 'Failed to load');
        return payload;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Flatten all items into products grouped by vendor + canonical_name
  const products = useMemo(() => {
    if (!data?.vendors) return [];
    const map = {};

    for (const vendor of data.vendors) {
      for (const bill of vendor.bills) {
        for (const item of bill.items) {
          if (!item.enriched) continue;

          const key = item.product_hash
            ? `${vendor.vendor_id}#${item.product_hash}`
            : `${vendor.vendor_id}#${item.standardized_name || item.description}`;

          if (!map[key]) {
            map[key] = {
              key,
              name: item.standardized_name || item.description,
              category: item.category,
              subcategory: item.subcategory,
              brand: item.brand,
              form: item.form,
              grade: item.grade,
              item_type: item.item_type,
              unit_normalized: item.unit_normalized,
              unit_descriptor: item.unit_descriptor,
              vendor_sku: item.vendor_sku,
              variant_descriptor: item.variant_descriptor,
              vendor_id: vendor.vendor_id,
              vendor_name: vendor.vendor_name,
              purchases: [],
              latest_price: null,
              price_trend: null,
            };
          }

          map[key].purchases.push({
            bill_number: bill.bill_number,
            bill_date: bill.bill_date,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.unit_price,
            extended_amount: item.extended_amount,
            price_per_unit: item.price_per_unit,
            unit_normalized: item.unit_normalized,
            purchase_summary: item.purchase_summary,
            price_vector: item.price_vector,
          });
        }
      }
    }

    // Helper: get the primary price from a purchase's price vector
    const getPrimary = (p) => {
      const pv = p?.price_vector;
      if (!pv || pv.length === 0) return { price: p?.price_per_unit || p?.unit_price, unit: p?.unit_normalized || p?.unit };
      const primary = pv.find(v => v.is_primary) || pv[0];
      return { price: primary.price, unit: primary.unit };
    };

    // Sort purchases by date desc, compute trend
    return Object.values(map).map(product => {
      product.purchases.sort((a, b) => (b.bill_date || '').localeCompare(a.bill_date || ''));

      // Use the purchase with the most price vector tiers to set the product's normalized unit
      const richest = product.purchases.reduce((best, p) =>
        (p.price_vector?.length || 0) > (best.price_vector?.length || 0) ? p : best
      , product.purchases[0]);
      const richestPrimary = getPrimary(richest);
      product.unit_normalized = richestPrimary.unit || product.unit_normalized;

      const latest = product.purchases[0];
      const previous = product.purchases[1];
      const latestPrimary = getPrimary(latest);
      const previousPrimary = getPrimary(previous);
      product.latest_price = latestPrimary.price;
      product.latest_date = latest?.bill_date;
      product.times_seen = product.purchases.length;

      if (latest && previous && latestPrimary.price && previousPrimary.price) {
        // Compare using the same unit — find matching unit in both vectors
        const latestUnit = richestPrimary.unit;
        const lp = latest.price_vector?.find(v => v.unit === latestUnit)?.price || latestPrimary.price;
        const pp = previous.price_vector?.find(v => v.unit === latestUnit)?.price || previousPrimary.price;
        if (lp && pp && pp !== 0) {
          const pctChange = ((lp - pp) / pp) * 100;
          product.price_trend = { pct: pctChange, direction: pctChange > 0.5 ? 'up' : pctChange < -0.5 ? 'down' : 'stable' };
        }
      }

      return product;
    }).sort((a, b) => (b.times_seen || 0) - (a.times_seen || 0));
  }, [data]);

  // Collect filters
  const allCategories = useMemo(() => [...new Set(products.map(p => p.category).filter(Boolean))].sort(), [products]);
  const allVendors = useMemo(() => {
    const map = {};
    products.forEach(p => { if (p.vendor_id) map[p.vendor_id] = p.vendor_name; });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);

  // Filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter(p => {
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      if (vendorFilter !== 'all' && p.vendor_id !== vendorFilter) return false;
      if (!q) return true;
      return (p.name || '').toLowerCase().includes(q)
        || (p.vendor_name || '').toLowerCase().includes(q)
        || (p.brand || '').toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q);
    });
  }, [products, search, categoryFilter, vendorFilter]);

  // Group by vendor
  const vendorGroups = useMemo(() => {
    const groups = {};
    for (const p of filtered) {
      const vid = p.vendor_id || '__unknown__';
      if (!groups[vid]) groups[vid] = { vendor_name: p.vendor_name, products: [] };
      groups[vid].products.push(p);
    }
    return Object.entries(groups).sort((a, b) => b[1].products.length - a[1].products.length);
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-20"><p className="text-sm text-red-500">{error}</p></div>;
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '100ms' }}
      >
        <h1 className="text-[22px] font-semibold text-zinc-900">Purchases</h1>
        <p className="text-sm text-zinc-400 mt-1">Products you buy, grouped by vendor, with price trends</p>
      </div>

      {/* Search + Filters */}
      <div
        className="flex flex-col sm:flex-row gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '150ms' }}
      >
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products, vendors, brands..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-400 transition-colors"
          />
        </div>
        <select
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-400"
        >
          <option value="all">All Vendors</option>
          {allVendors.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md whitespace-nowrap transition-colors ${
              categoryFilter === 'all' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            All
          </button>
          {allCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
              className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md whitespace-nowrap transition-colors ${
                categoryFilter === cat ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Product list grouped by vendor */}
      <div
        className="space-y-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '200ms' }}
      >
        {vendorGroups.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg py-12 text-center">
            <p className="text-sm text-zinc-400">{search || categoryFilter !== 'all' ? 'No matching products' : 'No purchase data yet'}</p>
          </div>
        ) : vendorGroups.map(([vid, { vendor_name, products: vProducts }]) => (
          <div key={vid} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            {/* Vendor header */}
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-500">
                  {(vendor_name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-900">{vendor_name}</p>
                  <p className="text-[11px] text-zinc-400">{vProducts.length} product{vProducts.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Product table */}
            <div className="divide-y divide-zinc-100">
              {/* Header */}
              <div className="grid grid-cols-[1fr_90px_100px_60px_70px] gap-3 px-5 py-2 text-[9px] uppercase tracking-widest text-zinc-400 font-medium bg-zinc-50/60">
                <span>Product</span>
                <span>Category</span>
                <span className="text-right">Price</span>
                <span className="text-center">Trend</span>
                <span className="text-center">Orders</span>
              </div>

              {vProducts.map(product => {
                const isOpen = expandedProduct === product.key;
                const trend = product.price_trend;

                return (
                  <div key={product.key}>
                    <button
                      onClick={() => setExpandedProduct(isOpen ? null : product.key)}
                      className={`w-full grid grid-cols-[1fr_90px_100px_60px_70px] gap-3 px-5 py-2.5 text-left transition-colors ${
                        isOpen ? 'bg-zinc-50' : 'hover:bg-zinc-50/50'
                      }`}
                    >
                      {/* Product name */}
                      <div className="min-w-0">
                        <p className="text-sm text-zinc-800 truncate">{product.name}</p>
                        {product.brand && <p className="text-[11px] text-zinc-400 mt-0.5">{product.brand}</p>}
                      </div>

                      {/* Category */}
                      <div className="self-center">
                        {product.category && (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium truncate max-w-full ${getCatColor(product.category)}`}>
                            {product.category}
                          </span>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-right self-center">
                        <span className="text-sm font-mono text-zinc-800">{fmt(product.latest_price)}</span>
                        {product.unit_normalized && (
                          <span className="text-[10px] text-zinc-400 ml-0.5">/{product.unit_normalized}</span>
                        )}
                      </div>

                      {/* Trend */}
                      <div className="text-center self-center">
                        {trend ? (
                          <span className={`text-[11px] font-medium ${
                            trend.direction === 'up' ? 'text-red-500' : trend.direction === 'down' ? 'text-emerald-500' : 'text-zinc-400'
                          }`}>
                            {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '—'}
                            {trend.direction !== 'stable' ? ` ${Math.abs(trend.pct).toFixed(0)}%` : ''}
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </div>

                      {/* Orders */}
                      <div className="text-center self-center">
                        <span className="text-xs text-zinc-500">{product.times_seen}</span>
                      </div>
                    </button>

                    {/* Expanded: Value Line style report */}
                    {isOpen && (() => {
                      // Build chart data with all price vector tiers
                      const reversed = [...product.purchases].reverse();
                      const vectorKeys = new Set();
                      const chartData = reversed.map(p => {
                        const row = {
                          date: p.bill_date,
                          label: fmtDate(p.bill_date),
                        };
                        if (p.price_vector && p.price_vector.length > 0) {
                          for (const pv of p.price_vector) {
                            const key = pv.unit;
                            vectorKeys.add(key);
                            row[key] = pv.price;
                            row[`${key}_label`] = pv.label;
                            row[`${key}_primary`] = pv.is_primary;
                          }
                        } else {
                          const fallbackUnit = p.unit_normalized || p.unit || 'ea';
                          vectorKeys.add(fallbackUnit);
                          row[fallbackUnit] = p.price_per_unit || p.unit_price;
                        }
                        return row;
                      });
                      // Sort vector keys: primary first, then by price descending
                      const sortedVectorKeys = [...vectorKeys].sort((a, b) => {
                        const aP = chartData[chartData.length - 1]?.[`${a}_primary`];
                        const bP = chartData[chartData.length - 1]?.[`${b}_primary`];
                        if (aP && !bP) return -1;
                        if (!aP && bP) return 1;
                        return (chartData[chartData.length - 1]?.[b] || 0) - (chartData[chartData.length - 1]?.[a] || 0);
                      });
                      const VECTOR_COLORS = ['#3b5bdb', '#0ca678', '#f59f00', '#e8590c', '#ae3ec9', '#1098ad'];
                      const primaryKey = sortedVectorKeys.find(k => chartData[chartData.length - 1]?.[`${k}_primary`]) || sortedVectorKeys[0];
                      const prices = chartData.map(d => d[primaryKey]).filter(Boolean);
                      const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
                      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                      const totalSpend = product.purchases.reduce((s, p) => s + (parseFloat(p.extended_amount) || 0), 0);
                      const unit = product.unit_normalized || product.purchases[0]?.unit || 'ea';

                      return (
                        <div className="border-t border-zinc-200 bg-white">
                          {/* Top bar: key stats */}
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-zinc-100 border-b border-zinc-200">
                            {[
                              { label: 'Current', value: `${fmt(product.latest_price)}/${unit}` },
                              { label: 'Average', value: `${fmt(avgPrice)}/${unit}` },
                              { label: 'Low / High', value: `${fmt(minPrice)} – ${fmt(maxPrice)}` },
                              { label: 'Total Spend', value: fmt(totalSpend) },
                              { label: 'Orders', value: product.times_seen },
                            ].map(s => (
                              <div key={s.label} className="bg-white px-4 py-2.5">
                                <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium">{s.label}</p>
                                <p className="text-sm font-semibold text-zinc-900 tabular-nums mt-0.5">{s.value}</p>
                              </div>
                            ))}
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px]">
                            {/* Left: History */}
                            <div className="border-r border-zinc-100">
                              {/* Value Line style table: metrics as rows, dates as columns */}
                              <div className="p-4 overflow-x-auto">
                                <table className="text-xs border-collapse">
                                  {/* Column headers: dates */}
                                  <thead>
                                    <tr className="border-b border-zinc-200">
                                      <th className="text-left text-[9px] uppercase tracking-widest text-zinc-400 font-medium py-1.5 pr-4 w-28" />
                                      {product.purchases.map((p, i) => (
                                        <th key={i} className={`text-right py-1.5 px-2 min-w-[70px] text-[10px] uppercase tracking-wider font-semibold ${i === 0 ? 'text-zinc-900' : 'text-zinc-500'}`}>
                                          {fmtDate(p.bill_date)}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* Bill # */}
                                    <tr className="border-b border-zinc-50">
                                      <td className="text-[10px] text-zinc-400 font-medium py-1 pr-4">BILL #</td>
                                      {product.purchases.map((p, i) => (
                                        <td key={i} className="text-right py-1 px-2 font-mono text-zinc-500 text-[11px]">#{p.bill_number || '--'}</td>
                                      ))}
                                    </tr>
                                    {/* Quantity */}
                                    <tr className="border-b border-zinc-50">
                                      <td className="text-[10px] text-zinc-400 font-medium py-1 pr-4">QTY ORDERED</td>
                                      {product.purchases.map((p, i) => (
                                        <td key={i} className="text-right py-1 px-2 tabular-nums text-zinc-600">
                                          {p.quantity} {p.unit || ''}
                                        </td>
                                      ))}
                                    </tr>
                                    {/* Total */}
                                    <tr className="border-b border-zinc-200">
                                      <td className="text-[10px] text-zinc-400 font-medium py-1 pr-4">TOTAL</td>
                                      {product.purchases.map((p, i) => (
                                        <td key={i} className={`text-right py-1 px-2 tabular-nums font-medium ${i === 0 ? 'text-zinc-900' : 'text-zinc-600'}`}>
                                          {fmt(p.extended_amount)}
                                        </td>
                                      ))}
                                    </tr>
                                    {/* Price vector rows — one row per unit tier */}
                                    {sortedVectorKeys.map((vk, vi) => (
                                      <tr key={vk} className={`border-b ${vi === 0 ? 'border-zinc-100' : 'border-zinc-50'}`}>
                                        <td className={`text-[10px] py-1 pr-4 font-medium ${vi === 0 ? 'text-zinc-700 uppercase tracking-wider' : 'text-zinc-400'}`}>
                                          per {vk}
                                        </td>
                                        {product.purchases.map((p, i) => {
                                          const pv = p.price_vector?.find(v => v.unit === vk);
                                          const isPrimary = pv?.is_primary;
                                          return (
                                            <td key={i} className={`text-right py-1 px-2 tabular-nums font-mono ${
                                              isPrimary ? (i === 0 ? 'text-zinc-900 font-bold' : 'text-zinc-700 font-semibold') : 'text-zinc-400'
                                            }`}>
                                              {pv ? fmt(pv.price) : '--'}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                    {/* % change rows — one per unit tier */}
                                    {sortedVectorKeys.map((vk, vi) => {
                                      // Only show % change for primary unit to keep it clean
                                      if (vi > 0) return null;
                                      return (
                                        <tr key={`pct-${vk}`} className="border-b border-zinc-100">
                                          <td className="text-[10px] py-1 pr-4 font-medium text-zinc-400">% chg</td>
                                          {product.purchases.map((p, i) => {
                                            const next = product.purchases[i + 1]; // next is older (purchases sorted newest first)
                                            if (!next) return <td key={i} className="text-right py-1 px-2 text-[10px] text-zinc-300">—</td>;
                                            const curr = p.price_vector?.find(v => v.unit === vk)?.price;
                                            const prev = next.price_vector?.find(v => v.unit === vk)?.price;
                                            if (!curr || !prev || prev === 0) return <td key={i} className="text-right py-1 px-2 text-[10px] text-zinc-300">—</td>;
                                            const pct = ((curr - prev) / prev) * 100;
                                            return (
                                              <td key={i} className={`text-right py-1 px-2 tabular-nums text-[10px] font-medium ${
                                                pct > 0.5 ? 'text-red-500' : pct < -0.5 ? 'text-emerald-500' : 'text-zinc-400'
                                              }`}>
                                                {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                    {/* Volume/weight quantity rows — one per unit tier */}
                                    {sortedVectorKeys.length > 0 && (
                                      <tr className="border-b border-zinc-200"><td colSpan={product.purchases.length + 1} className="py-0.5" /></tr>
                                    )}
                                    {sortedVectorKeys.map((vk, vi) => {
                                      const perPurchase = product.purchases.map(p => {
                                        const pv = p.price_vector?.find(v => v.unit === vk);
                                        return pv ? Number(pv.divisor) || 0 : 0;
                                      });
                                      const cumulative = perPurchase.reduce((a, b) => a + b, 0);
                                      if (cumulative === 0) return null;
                                      return (
                                        <tr key={`qty-${vk}`} className="border-b border-zinc-50">
                                          <td className={`text-[10px] py-1 pr-4 font-medium ${vi === 0 ? 'text-zinc-700 uppercase tracking-wider' : 'text-zinc-400'}`}>
                                            total {vk}
                                          </td>
                                          {perPurchase.map((qty, i) => (
                                            <td key={i} className={`text-right py-1 px-2 tabular-nums ${vi === 0 ? 'text-zinc-700' : 'text-zinc-400'}`}>
                                              {qty > 0 ? `${Number.isInteger(qty) ? qty : qty.toFixed(1)}` : '--'}
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    })}
                                    {/* Cumulative totals row */}
                                    {sortedVectorKeys.length > 0 && (() => {
                                      const primaryTotals = product.purchases.reduce((sum, p) => {
                                        const pv = p.price_vector?.find(v => v.unit === primaryKey);
                                        return sum + (pv ? Number(pv.divisor) || 0 : 0);
                                      }, 0);
                                      if (primaryTotals === 0) return null;
                                      return (
                                        <tr className="border-t border-zinc-200">
                                          <td className="text-[10px] text-zinc-900 font-semibold uppercase tracking-wider py-1.5 pr-4">
                                            All-Time {primaryKey}
                                          </td>
                                          <td colSpan={product.purchases.length} className="text-right py-1.5 px-2 tabular-nums text-zinc-900 font-semibold">
                                            {Number.isInteger(primaryTotals) ? primaryTotals : primaryTotals.toFixed(1)} {primaryKey}
                                          </td>
                                        </tr>
                                      );
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Right: Product details sidebar */}
                            <div className="p-4 space-y-5">
                              {/* Identity */}
                              <div>
                                <h4 className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Product</h4>
                                <div className="space-y-1.5 text-xs">
                                  {product.brand && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Brand</span><span className="text-zinc-700 text-right">{product.brand}</span></div>}
                                  {product.form && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Form</span><span className="text-zinc-700 text-right">{product.form}</span></div>}
                                  {product.grade && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Grade</span><span className="text-zinc-700 text-right">{product.grade}</span></div>}
                                  {product.unit_descriptor && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Pack size</span><span className="text-zinc-700 text-right">{product.unit_descriptor}</span></div>}
                                  {product.variant_descriptor && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Variant</span><span className="text-zinc-700 text-right">{product.variant_descriptor}</span></div>}
                                  {product.vendor_sku && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">SKU</span><span className="font-mono text-zinc-600 text-right">{product.vendor_sku}</span></div>}
                                  {product.category && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Category</span><span className="text-zinc-700 text-right">{product.category}</span></div>}
                                  {product.subcategory && <div className="flex justify-between gap-2"><span className="text-zinc-400 shrink-0">Subcategory</span><span className="text-zinc-700 text-right">{product.subcategory}</span></div>}
                                </div>
                              </div>

                              {/* Latest price breakdown */}
                              {product.purchases[0]?.price_vector?.length > 1 && (
                                <div>
                                  <h4 className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Latest Price Breakdown</h4>
                                  <div className="space-y-1.5">
                                    {product.purchases[0].price_vector.map((pv, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs">
                                        <span className="text-zinc-500">{pv.label}</span>
                                        <span className={`font-mono tabular-nums ${pv.is_primary ? 'text-zinc-900 font-medium' : 'text-zinc-600'}`}>
                                          {fmt(pv.price)}/{pv.unit}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Purchase summary */}
                              {product.purchases[0]?.purchase_summary && (
                                <div>
                                  <h4 className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Summary</h4>
                                  <p className="text-xs text-zinc-600 leading-relaxed">{product.purchases[0].purchase_summary}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorCatalogView;
